import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import MUITable from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import SearchIcon from '@mui/icons-material/Search';
import isEqual from 'lodash/isEqual';
import {
  Dispatch,
  ReactElement,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

import { useStoreActions, useStoreState } from '~/store/hooks';
import useRole from '~/store/user/hooks/useRole';
import { IClientsFilters } from '~/store/client/types';
import { extractErrorMessage } from '~/utils/error/error';
import formatActTeamsOptions from '~/utils/formatActTeamsOptions';
import useMemoCompare from '~/hooks/useMemoCompare';
import { IClient } from '~/services/api/client/types';
import { IClientsParams, IParams, Sorting } from '~/services/api/types';
import Loader from '~/ui/components/common/Loader';
import AutoSubmit from '~/ui/components/inputs/AutoSubmit';
import Input from '~/ui/components/inputs/Input';
import Select from '~/ui/components/inputs/SelectWithoutAnimation';

import { red } from '~/ui/constants/colors';
import { VIEW_CLIENT } from '~/ui/constants/paths';
import { sortingOptions, statusesOptions } from '~/ui/constants/sortingOptions';

import ClientManagementTableBody from '~/ui/pages/Clients/LocalAdmin/ClientsManagement/ClientManagementTableBody';
import { IClientAction } from './types';

import styles from './Table.module.scss';

const defaultValues = {
  name: '',
  sorting: Sorting.ByName,
  status: 0,
};

let initialized = false;

interface IGetClientsPayload {
  clinicId: string;
  teamId: string;
  teamIds?: string[];
  params: IParams;
}

interface IProps {
  showTeamSelect?: boolean;
  loading?: boolean;
  list: IClient[];
  clinicId: number;
  teamId?: number;
  onGetClients: (payload: IGetClientsPayload) => Promise<unknown>;
  columnNames?: string[];
  setClientAction?: Dispatch<SetStateAction<IClientAction>>;
  withActions?: boolean;
}

const Table = ({
  list,
  showTeamSelect = false,
  loading: dataLoading,
  clinicId,
  teamId,
  onGetClients,
  setClientAction,
  withActions,
}: IProps): ReactElement => {
  const [loading, setLoading] = useState(false);
  const {
    isGlobalUser,
    role: { teams: userAvailableTeams = [] },
  } = useRole();

  const filters = useStoreState(state => state.client.filters);

  const memoizedFilters = useMemoCompare(filters, (prev, next) => prev && isEqual(prev, next));

  const filteredTeamIds =
    memoizedFilters.teamIds.filter(
      filterTeamId =>
        userAvailableTeams.some(userTeam => userTeam.id === filterTeamId) ||
        !userAvailableTeams.length,
    ) || [];

  const formMethods = useForm({
    defaultValues: {
      ...(memoizedFilters || defaultValues),
      teams: filteredTeamIds.length ? filteredTeamIds : userAvailableTeams.map(team => team.id),
    },
  });

  const pagination = useStoreState(state => state.client.pagination);

  const { availableActTeams } = useStoreState(state => state.actTeam);

  const showError = useStoreActions(actions => actions.snackbar.showError);

  const {
    watch,
    control,
    register,
    setValue,
    formState: { errors },
  } = formMethods;

  const watchValues = watch();

  const onSubmit = async ({ teams, teamIds, ...params }: IClientsParams) => {
    if (!clinicId) {
      setLoading(false);
      return;
    }

    try {
      const requestPayload = {
        clinicId: String(clinicId),
        teamId: teamId ? String(teamId) : undefined,
        teamIds: teams,
        params: { ...params, pageSize: pagination.pageSize, pageNumber: 1 },
      };

      await onGetClients(requestPayload);
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const handlePagination = async (pageSize: number, pageNumber: number) => {
    if (clinicId) {
      try {
        setLoading(true);
        const { teams, teamIds, ...restValues } = watchValues as IClientsFilters & {
          teams: number[];
        };
        await onGetClients({
          clinicId: String(clinicId),
          teamId: teamId ? String(teamId) : undefined,
          // teams here is number[] though the payload declares teamIds as string[]
          teamIds: teams as unknown as string[],
          params: { ...restValues, pageSize, pageNumber },
        });
      } catch (e) {
        showError(extractErrorMessage(e));
      } finally {
        setLoading(false);
      }
    }
  };

  // format team object for select component
  const filteredTeamsOptions = useMemo(
    () => formatActTeamsOptions(availableActTeams),
    [availableActTeams],
  );

  // select all items in select component
  const setAll = useCallback(
    (all?: boolean) => {
      if (all)
        setValue(
          'teams',
          filteredTeamsOptions.map(m => Number(m.value)),
        );
      else {
        setValue('teams', []);
      }
    },
    [filteredTeamsOptions, setValue],
  );

  const { teams, status } = watchValues;

  const renderItem = (item: IClient) => {
    const getClientTeam = () => {
      if (isGlobalUser) {
        return (
          item.teams.find(team => userAvailableTeams.some(t => t.id === team.id)) || item.teams[0]
        );
      }

      const activeTeam = item.teams.find(t => !t.isArchived && teams.includes(t.team?.id));

      return (
        activeTeam ||
        item.teams.find(
          team => teams.includes(team.team.id) || teams.includes(team.team.id) || team.isPrimary,
        ) ||
        item.teams[0]
      );
    };

    const clientTeam = getClientTeam();

    return (
      <TableRow key={item.id}>
        <TableCell>
          <Link
            className={styles.link}
            to={VIEW_CLIENT.replace(':clientId', String(item.id)).replace(
              ':actTeamId',
              String(clientTeam?.team.id),
            )}
            state={{ actTeamId: String(clientTeam?.team.id) }}
          >
            {item.firstName}
          </Link>
        </TableCell>
        <TableCell>{item.lastName}</TableCell>
        <TableCell>{item.email}</TableCell>
        <ClientManagementTableBody
          filters={{ teams, status }}
          item={item}
          setClientAction={setClientAction}
          withActions={withActions}
        />
      </TableRow>
    );
  };

  // select all teams on mount
  useEffect(() => {
    if (filteredTeamsOptions && !memoizedFilters.teamIds?.length && !initialized) {
      setAll(true);
      initialized = true;
    }

    if (memoizedFilters.teamIds?.length && !initialized) {
      setValue('teams', memoizedFilters.teamIds);
      initialized = true;
    }
  }, [filteredTeamsOptions, memoizedFilters.teamIds, setAll, setValue]);

  useEffect(
    () => () => {
      initialized = false;
    },
    [],
  );

  return (
    <TableContainer className={styles.tableContainer}>
      {loading || dataLoading ? <Loader className={styles.loader} /> : null}
      <Box sx={{ p: 2 }}>
        <FormProvider {...formMethods}>
          <form>
            <Grid container spacing={1}>
              {showTeamSelect && (
                <Grid
                  className={styles.multiSelect}
                  size={{
                    sm: 3,
                    md: 3,
                  }}
                >
                  <Select
                    isMulti
                    oneLineMulti
                    closeMenuOnSelect={false}
                    showCustomComponents
                    showSelectAll
                    hideSelectedOptions={false}
                    name="teams"
                    options={filteredTeamsOptions}
                    control={control}
                    errors={errors}
                    label="Select Team"
                    setAll={setAll}
                  />
                </Grid>
              )}
              <Grid
                size={{
                  sm: 4,
                }}
              >
                <Input
                  register={register}
                  placeholder="Search clients"
                  name="name"
                  errors={errors}
                  startAdornment={<SearchIcon color="primary" />}
                />
              </Grid>
              <Grid
                size={{
                  sm: 2,
                }}
              >
                <Select
                  label="Sort By"
                  control={control}
                  name="sorting"
                  errors={errors}
                  options={sortingOptions}
                  color={red}
                  hideSelectedOptions={false}
                />
              </Grid>
              <Grid
                size={{
                  sm: 2,
                }}
              >
                <Select
                  label="Filter By Status"
                  control={control}
                  name="status"
                  errors={errors}
                  options={statusesOptions}
                  color={red}
                  hideSelectedOptions={false}
                />
              </Grid>
              <AutoSubmit
                debounce={350}
                initialValues={defaultValues}
                onSubmit={onSubmit}
                setLoading={setLoading}
              />
            </Grid>
          </form>
        </FormProvider>
      </Box>
      <MUITable>
        <TableHead>
          <TableRow>
            <TableCell>First Name</TableCell>
            <TableCell>Last Name</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Assigned Teams</TableCell>
            <TableCell>Status</TableCell>
            {withActions && <TableCell>Actions</TableCell>}
          </TableRow>
        </TableHead>
        {!loading && !dataLoading && <TableBody>{list.map(renderItem)}</TableBody>}
      </MUITable>
      <TablePagination
        component="div"
        count={pagination.total}
        onPageChange={(_, pageNumber) => handlePagination(pagination.pageSize, pageNumber + 1)}
        onRowsPerPageChange={e => handlePagination(Number(e.target.value), 1)}
        page={pagination.pageNumber - 1}
        rowsPerPage={pagination.pageSize}
        rowsPerPageOptions={[5, 10, 25]}
      />
    </TableContainer>
  );
};

export default Table;
